import { isBlockedForProspecting } from "./crm-v2-policy.mjs";
import { normalizeClient, scheduleWorkingDayTask } from "./commercial.mjs";
import { todayParis } from "../utils/calendar.mjs";

// Server-confirmed offers only. Existing client owners and validated needs stay unchanged.
export function mergeFranceTravailProspects(state, rows) {
  if (!state || !Array.isArray(rows)) return state;
  let next = state;
  for (const row of rows) {
    if (!row.clientId || !row.reference || !row.title) continue;
    const key = String(row._id);
    let client = next.clients.find(
      (c) =>
        String(c.client_id || c.id) === row.clientId ||
        (row.siret && c.siret === row.siret),
    );
    if (!client) {
      client = normalizeClient({
        id: row.clientId,
        client_id: row.clientId,
        establishment_id: row.clientId,
        name: row.company,
        siret: row.siret,
        city: row.city,
        postCode: row.postCode,
        postalCode: row.postCode,
        address: row.address,
        agency: row.agency,
        ownerId: row.ownerId,
        owner: row.ownerName || "",
        source: row.source || "France Travail",
        kind: "prospect",
        status: "PROSPECT",
        contacts: [],
        history: [],
      });
      next = { ...next, clients: [...next.clients, client] };
    }
    if (isBlockedForProspecting(client)) continue;
    const existing = next.needs.find((need) => need.franceTravailKey === key);
    if (existing) {
      if (existing.lastSeenAt !== row.lastSeenAt || JSON.stringify(existing.origins || []) !== JSON.stringify(row.origins || []))
        next = {
          ...next,
          needs: next.needs.map((n) =>
            n.id === existing.id ? { ...n, lastSeenAt: row.lastSeenAt, origins: row.origins || [], announcements: row.announcements || [] } : n,
          ),
        };
      continue;
    }
    const id = `ft-need:${key}`;
    const type = /cdi/i.test(row.contract)
      ? "CDI"
      : /cdd/i.test(row.contract)
        ? "CDD"
        : /vacation/i.test(row.contract)
          ? "Vacation"
          : /interim|intérim/i.test(row.contract)
            ? "Intérim"
            : "";
    const createdAt = row.detectedAt || new Date().toISOString();
    const need = {
      id,
      clientId: client.id,
      establishment_id: client.id,
      client: client.name,
      franceTravailKey: key,
      job: row.title,
      qty: row.quantity ?? null,
      type,
      source: row.source || "France Travail",
      origins: row.origins || [], announcements: row.announcements || [],
      sourceUrl: row.url,
      offerReference: row.reference,
      publishedAt: row.publishedAt,
      lastSeenAt: row.lastSeenAt,
      duration: row.duration,
      status: "à qualifier",
      createdAt,
      ownerId: client.ownerId,
      agency: client.agency,
      start: "",
      hours: "",
      urgency: "",
      notes: `Offre publique ${row.source || "France Travail"} ${row.reference} — ${row.url}\n${row.description || ""}${row.quantity == null ? "\nNombre de postes à confirmer." : ""}${!type ? "\nType de service à qualifier." : ""}`,
    };
    const signal = {
      id: `ft-signal:${key}`,
      franceTravailKey: key,
      clientId: client.id,
      source: row.source || "France Travail",
      date: row.publishedAt || todayParis(new Date(createdAt)),
      createdAt,
      job: row.title,
      qty: row.quantity ?? null,
      type: "besoin identifié",
      content: `Recrutement public : ${row.title} — ${row.url}`,
      origins: row.origins || [], announcements: row.announcements || [],
      sourceUrl: row.url,
    };
    next = {
      ...next,
      needs: [...next.needs, need],
      signals: [...next.signals, signal],
    };
    const qualification = next.tasks.find((task) =>
      task.clientId === client.id && task.category === "need" && !task.done &&
      !task.cancelled && !["annulé", "annule", "cancelled"].includes(String(task.status || "").toLowerCase()));
    if (qualification) {
      next = { ...next, tasks: next.tasks.map((task) => task.id === qualification.id ? {
        ...task,
        needIds: [...new Set([...(task.needIds || []), task.needId, id].filter(Boolean))],
      } : task) };
      continue;
    }
    next = scheduleWorkingDayTask(
      next,
      client.id,
      `Qualifier le besoin : ${row.title}`,
      todayParis(),
      "need",
      { needId: id },
    );
  }
  return next;
}

// Keep server-confirmed imports visible independently of the wizard lifecycle.
export function watchFranceTravailProspects(api, onRows, intervalMs = 5000) {
  let active = true;
  let pending = false;
  const refresh = async () => {
    if (!active || pending) return;
    pending = true;
    try {
      const { data } = await api.get("commercial/france-travail/needs");
      if (active) onRows(data);
    } catch {
      // Preserve the current portfolio; the next poll retries a transient failure.
    } finally {
      pending = false;
    }
  };
  refresh();
  const timer = setInterval(refresh, intervalMs);
  return () => { active = false; clearInterval(timer); };
}
